import { useEffect, useState } from 'react';
import type { AdminUserRow } from '@chicare/shared';

import { useAdminUsers } from '@/lib/onboardingQueries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

const ROLES = [
  ['all', 'Everyone'],
  ['buyer', 'Buyers'],
  ['pharmacy', 'Pharmacies'],
  ['rider', 'Riders'],
  ['admin', 'Staff'],
] as const;

/**
 * The register of people.
 *
 * ── WHAT THIS SCREEN IS FOR ─────────────────────────────────────────────────
 *
 * Support's most common call is somebody who cannot sign in, and the cause is
 * nearly always the same: the email on the account has a typo, or the number
 * they are calling from is not the one on it. Until now there was nowhere in
 * ChiCare to check that, so the only answer available was "I can't see your
 * account" — which to the person on the phone is indistinguishable from "you
 * don't have one".
 *
 * ── WHY THE CONTACTS ARE SHOWN IN FULL ──────────────────────────────────────
 *
 * Masking them would defeat the purpose. The job is to read a contact back to a
 * caller and find out it is wrong.
 *
 * ── WHY NOTHING HERE CAN BE EDITED ──────────────────────────────────────────
 *
 * An operator who can silently repoint the email on somebody else's account can
 * take that account — one-time codes make possession of a channel the whole
 * proof of identity. There is no edit control here because there is no edit
 * endpoint behind it. Changing a contact belongs in a flow with its own
 * verification and its own audit trail.
 */
export function UsersScreen() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<string>('all');

  // Debounced, so reading a phone number out loud does not fire eleven
  // searches while the operator types it.
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 300);

    return () => window.clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isError, error, refetch } = useAdminUsers(query, role);

  if (isError) {
    return <AdminError title="We couldn't load the register" error={error} onRetry={() => refetch()} />;
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-8">
      <header className="mb-5">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">People</h1>
        <p className="mt-1 max-w-[70ch] text-[13px] leading-relaxed text-muted">
          Everyone with a ChiCare account, and both of the ways we can reach them. Search by name,
          email, or the number a caller reads out to you. Nothing here can be edited — changing
          somebody&apos;s contact is how an account gets taken.
        </p>
      </header>

      <div className="mb-3">
        <label className="relative block">
          <span className="sr-only">Search people</span>
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, email, or 0803 123 4567"
            className="min-h-11 w-full rounded-btn border border-line bg-white pl-9 pr-3 text-[14px] text-ink outline-none focus:border-blue"
          />
        </label>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {ROLES.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setRole(key)}
            className={cn(
              'min-h-9 rounded-full border px-3 text-[12.5px] font-semibold',
              role === key
                ? 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
            {meta?.counts?.[key] !== undefined ? (
              <span className="ml-1.5 opacity-70">{meta.counts[key]}</span>
            ) : null}
          </button>
        ))}
      </div>

      {/* The number that should trend to zero. Buyers who registered before
          both contacts were required are reachable on only one channel, and
          each of them is one bad SMS away from being locked out. */}
      {meta && meta.missing_a_contact > 0 ? (
        <div className="mb-5 border-y border-line py-3.5">
          <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            Reachable on one channel only
          </p>
          <p className="m-0 mt-1.5 text-[13.5px] leading-relaxed text-ink">
            <b className="font-mono text-[15px] tabular-nums">{meta.missing_a_contact}</b>{' '}
            {meta.missing_a_contact === 1 ? 'buyer' : 'buyers'} registered before both a number and
            an email were required. Each is asked for the missing one every time they sign in.
          </p>
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} data-skeleton className="h-[72px] w-full" radius="card" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={query ? 'Nobody matches that' : 'Nobody here yet'}
          body={
            query
              ? 'Try their other contact — plenty of people give us a number on one device and an email on another.'
              : 'Accounts appear here the moment somebody registers.'
          }
          action={
            query ? (
              <Button variant="grey" onClick={() => setSearch('')}>
                Clear the search
              </Button>
            ) : (
              <Button variant="grey" onClick={() => refetch()}>
                Refresh
              </Button>
            )
          }
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-card border border-line bg-white">
            {rows.map((user) => (
              <UserRow key={user.id} user={user} />
            ))}
          </div>

          <p className="m-0 mt-3 text-[11.5px] text-faint">
            Showing {rows.length} of {meta?.total ?? rows.length}
            {rows.length === 200 ? ' — narrow the search to see more' : ''}.
          </p>
        </>
      )}
    </div>
  );
}

function UserRow({ user }: { user: AdminUserRow }) {
  return (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-2 border-b border-line px-4 py-3 last:border-b-0">
      <div className="min-w-[180px] flex-1">
        <p className="m-0 text-[14px] font-semibold text-ink">{user.name}</p>
        <p className="m-0 mt-0.5 text-[11.5px] text-faint">
          {user.role} · joined{' '}
          {user.created_at ? new Date(user.created_at).toLocaleDateString('en-NG') : 'unknown'}
        </p>
      </div>

      <div className="min-w-[200px] flex-1">
        <Contact
          value={user.phone}
          verified={user.phone_verified}
          missing="No phone number"
          mono
        />
        <Contact
          value={user.email}
          verified={user.email_verified}
          missing="No email address"
          className="mt-1"
        />
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Badge tone={user.buyer_tier === 'new' ? 'neutral' : 'verified'}>
          {user.buyer_tier_label}
        </Badge>
        <span className="text-[12px] text-muted">
          {user.orders_count} order{user.orders_count === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );
}

/**
 * One contact, and whether anybody has ever proved it.
 *
 * The tick is not decoration. An operator about to say "I'll resend it to your
 * email" needs to know whether a code has ever actually been answered there.
 */
function Contact({
  value,
  verified,
  missing,
  mono = false,
  className,
}: {
  value: string | null;
  verified: boolean;
  missing: string;
  mono?: boolean;
  className?: string;
}) {
  if (!value) {
    return (
      <p className={cn('m-0 text-[12.5px] italic text-danger', className)}>{missing}</p>
    );
  }

  return (
    <p className={cn('m-0 flex items-center gap-1.5 text-[12.5px] text-ink', className)}>
      <Icon
        name={verified ? 'shield' : 'clock'}
        size={12}
        filled={verified}
        className={cn('shrink-0', verified ? 'text-verified' : 'text-faint')}
      />
      <span className={cn('min-w-0 break-all', mono && 'font-mono')}>{value}</span>
      {!verified ? <span className="shrink-0 text-[11px] text-faint">unconfirmed</span> : null}
    </p>
  );
}
